import React, { FC, MutableRefObject, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import useIsMountedRef from 'use-is-mounted-ref'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import { PaginationInfo } from '../../../../NonReact/Types/Types'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { AdminFilters, AdminFilterValues } from '../../Molecules/AdminFilters/AdminFilters'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { DataTable, DataTableColumn } from '../../Molecules/DataTable/DataTable'
import { displayError } from '../../../../React/Common/Utils/Messages'
import {
    GenericAdminFilterLanguage,
    GenericAdminListLanguage,
    RecordingListLanguage,
    Router as RouterLang,
    SidebarMenu as SidebarMenuLanguage
} from '../../../../../i18n'
import { DEFAULT_ROWS_PER_PAGE, UseAdminStatState, useAdminTableState } from '../../../Hooks/useAdminStatState'
import { useAdminListLegacyServletSwitch } from '../../../Hooks/useAdminListLegacyServletSwitch'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { IsVarEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { ServletCmds } from '../../../../NonReact/constants/ServletCommands'
import { buildAdminListRowKey } from '../../../Utils/adminListRowKeys'
import { getRecordingList } from '../../../../React/Common/API/ItemsGetters'
import {
    RecordingList as RecordingRows,
    RecordingListItem,
    RecordingListResponse,
    RecordingListRow
} from '../../../../NonReact/Types/RecordingListTypes'
import { ALL_VALUE, ANY_VALUE_STRING_NUMERIC } from '../../../../React/Common/Constants/DefaultValues'
import { DEFAULT_CME_ID } from '../../../../React/Common/Constants/ServerValues'
import ModifyCallRecIntegration from '../../../../React/CallRecIntegrationModal/Modal/CallRecIntegration'
import { deleteCallRecordingIntegrationsMany } from '../../../../NonReact/common/API/EntityAPIClient'
import { AdminListUrlBootstrap, parseAdminListUrlSearchParams } from '../../../Utils/adminListUrlParams'
import { hasResolvedInitialTelSysPartitionListFilters } from '../../../Utils/adminListFilterReadiness'

const SCOPE_ALL: string = EMPTY_STRING
const SCOPE_SERVICEWIDE = '1' as const
const SCOPE_SYSTEMWIDE = '2' as const
const SCOPE_DETACHED = '10' as const

const TYPE_ALL: string = EMPTY_STRING
/** Dubber integration type id sent to the API (0). Not the legacy servlet list-view filter option (10). */
const TYPE_DUBBER = '0' as const

const isConcreteTelSysOrPartitionFilterValue = (value: unknown): boolean => {
    if (IsVarEmpty(value)) return false

    if (value === ALL_VALUE) return false

    if (value === ANY_VALUE_STRING_NUMERIC) return false

    return true
}

const getDescriptionOrIdValue = (description: string, id: number): string =>
    description !== EMPTY_STRING ? description : `${id}`

const getCallRecTypeValue = (language: CLanguageHandler, row: RecordingListRow): string => {
    if (row.CallRecIntTypeDescription !== EMPTY_STRING) return row.CallRecIntTypeDescription

    if (row.CallRecIntType === 0) return language.get(RecordingListLanguage.RecordingList_Type_Dubber)

    return `${row.CallRecIntType}`
}

export const RecordingList: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(RouterLang.NAME)
    language.loadBundle(SidebarMenuLanguage.NAME)
    language.loadBundle(GenericAdminListLanguage.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)
    language.loadBundle(RecordingListLanguage.NAME)

    const [recordingList, setRecordingList]: ReactState<Nullable<RecordingRows>> =
        useState<Nullable<RecordingRows>>(null)
    const {
        loading,
        setLoading,
        sort,
        setSort,
        filters,
        setFilters,
        pagination,
        setPagination,
        totalCount,
        setTotalCount
    }: UseAdminStatState = useAdminTableState()
    const [userInfo]: UserInfo = useUserInfo()
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [searchParams] = useSearchParams()
    const urlBootstrap: OnlyNullable<AdminListUrlBootstrap> = useMemo((): OnlyNullable<AdminListUrlBootstrap> => {
        const parsed: AdminListUrlBootstrap = parseAdminListUrlSearchParams(searchParams)

        return Object.keys(parsed).length > 0 ? parsed : null
    }, [searchParams])
    const [hasResolvedInitialUrlBootstrapLoad, setHasResolvedInitialUrlBootstrapLoad]: ReactState<boolean> =
        useState<boolean>(() => IsVarEmpty(urlBootstrap))
    const [hasResolvedInitialFiltersLoad, setHasResolvedInitialFiltersLoad]: ReactState<boolean> =
        useState<boolean>(false)
    const [hasReceivedInitialFilters, setHasReceivedInitialFilters]: ReactState<boolean> = useState<boolean>(false)
    const isBootstrappingFromUrl: boolean = isVarNotEmpty(urlBootstrap) && IsVarEmpty(filters)
    const isWaitingForInitialUrlBootstrapLoad: boolean =
        isVarNotEmpty(urlBootstrap) && !hasResolvedInitialUrlBootstrapLoad
    const isWaitingForInitialFiltersLoad: boolean = !hasResolvedInitialFiltersLoad

    const [isModalOpen, setModalOpen]: ReactState<boolean> = useState<boolean>(false)
    const [selectedRow, setSelectedRow]: ReactState<Nullable<RecordingListRow>> =
        useState<Nullable<RecordingListRow>>(null)
    const [viewOnly, setViewOnly]: ReactState<boolean> = useState<boolean>(false)

    const listCmeId: Nullable<string> = (filters?.cme?.value ?? userInfo?.Scope?.CMEnvironmentID) as Nullable<string>
    const listIncludeRecursive: boolean = filters?.includeCmeChildren ?? true
    const listSearch: string = filters?.search ?? EMPTY_STRING
    const scopeValue: string = String(filters?.scope?.value ?? SCOPE_ALL)
    const typeValue: string = String(filters?.codeType?.value ?? TYPE_ALL)
    const listNoTelephonyServer: boolean = filters?.noTelephonyServer ?? false
    const listTelSysId: Definable<string | number> = isConcreteTelSysOrPartitionFilterValue(
        filters?.telephonyServer?.value
    )
        ? (filters?.telephonyServer?.value)
        : undefined
    const listPartIDInTelSys: Definable<string | number> =
        isVarNotEmpty(listTelSysId) && isConcreteTelSysOrPartitionFilterValue(filters?.partition?.value)
            ? (filters?.partition?.value)
            : undefined
    const listScopeFilter: Definable<string> = scopeValue === SCOPE_ALL ? undefined : scopeValue
    const listTypeFilter: Definable<string> = typeValue === TYPE_ALL ? undefined : typeValue

    useAdminListLegacyServletSwitch(ServletCmds.ADMIN_CALLRECINTS, filters, userInfo?.Scope?.CMEnvironmentID)

    const getRecordingIntegrations = useCallback((): void => {
        if (!hasReceivedInitialFilters) return

        if (isBootstrappingFromUrl) return

        if (IsVarEmpty(listCmeId)) {
            setHasResolvedInitialUrlBootstrapLoad(true)
            setHasResolvedInitialFiltersLoad(true)
            return
        }

        setLoading((currentLoading: number): number => currentLoading + 1)

        if (listNoTelephonyServer) {
            if (isMounted.current) {
                setRecordingList([])
                setTotalCount(0)
            }
            setHasResolvedInitialUrlBootstrapLoad(true)
            setHasResolvedInitialFiltersLoad(true)
            setLoading((currentLoading: number): number => currentLoading - 1)
            return
        }

        const [currentPage, rowsPerPage]: PaginationInfo = pagination
        const pageStartIndex: number = currentPage * rowsPerPage
        const searchQuery: Definable<string> = listSearch === EMPTY_STRING ? undefined : listSearch

        getRecordingList(
            listCmeId,
            listIncludeRecursive,
            listTelSysId,
            listPartIDInTelSys,
            listTypeFilter,
            pageStartIndex,
            rowsPerPage,
            sort,
            searchQuery,
            listScopeFilter
        )
            .then((response: Nullable<RecordingListResponse>): void => {
                if (!isMounted.current) return

                const rows: RecordingRows =
                    response?.Data?.Items?.map(
                        (item: RecordingListItem, index: number): RecordingListRow => ({
                            key: buildAdminListRowKey(
                                [
                                    listCmeId,
                                    item.CallRecIntGUID,
                                    item.Description,
                                    item.CallRecIntType,
                                    item.TelSysID,
                                    item.PartIDInTelSys
                                ],
                                pageStartIndex,
                                index
                            ),
                            CallRecIntGUID: item.CallRecIntGUID ?? EMPTY_STRING,
                            Description: item.Description ?? EMPTY_STRING,
                            CallRecIntType: Number(item.CallRecIntType ?? 0),
                            CallRecIntTypeDescription: item.CallRecIntTypeDescription ?? EMPTY_STRING,
                            TelSysID: Number(item.TelSysID ?? ALL_VALUE),
                            TelSysDescription: item.TelSysDescription ?? EMPTY_STRING,
                            PartIDInTelSys: Number(item.PartIDInTelSys ?? ALL_VALUE),
                            PartitionDescription: item.PartitionDescription ?? EMPTY_STRING
                        })
                    ) ?? []

                setRecordingList(rows)
                setTotalCount(Number(response?.Data?.Total) || 0)
            })
            .catch(displayError)
            .finally((): void => {
                setHasResolvedInitialUrlBootstrapLoad(true)
                setHasResolvedInitialFiltersLoad(true)

                if (isMounted.current) setLoading((currentLoading: number): number => currentLoading - 1)
            })
    }, [
        hasReceivedInitialFilters,
        isBootstrappingFromUrl,
        listCmeId,
        listIncludeRecursive,
        listNoTelephonyServer,
        listSearch,
        listTelSysId,
        listPartIDInTelSys,
        listScopeFilter,
        listTypeFilter,
        pagination,
        sort
    ])

    useEffect(getRecordingIntegrations, [getRecordingIntegrations])

    useEffect((): void => {
        setHasResolvedInitialUrlBootstrapLoad(IsVarEmpty(urlBootstrap))
    }, [urlBootstrap])

    const scopeOptions = useMemo(
        (): { value: string; label: string }[] => [
            { value: SCOPE_ALL, label: language.get(RecordingListLanguage.RecordingList_Scope_All) },
            { value: SCOPE_SERVICEWIDE, label: language.get(RecordingListLanguage.RecordingList_Scope_ServiceWide) },
            { value: SCOPE_SYSTEMWIDE, label: language.get(RecordingListLanguage.RecordingList_Scope_SystemWide) },
            { value: SCOPE_DETACHED, label: language.get(RecordingListLanguage.RecordingList_Scope_Detached) }
        ],
        [language]
    )

    const typeOptions = useMemo(
        (): { value: string; label: string }[] => [
            { value: TYPE_ALL, label: language.get(RecordingListLanguage.RecordingList_Type_All) },
            { value: TYPE_DUBBER, label: language.get(RecordingListLanguage.RecordingList_Type_Dubber) }
        ],
        [language]
    )

    const columns: DataTableColumn<RecordingListRow, keyof RecordingListRow>[] = useMemo(
        (): DataTableColumn<RecordingListRow, keyof RecordingListRow>[] => [
            {
                headerName: language.get(RecordingListLanguage.RecordingList_Column_Description),
                field: 'Description',
                key: 'recording-list-description',
                sortable: true,
                align: 'left'
            },
            {
                headerName: language.get(RecordingListLanguage.RecordingList_Column_Type),
                field: 'CallRecIntTypeDescription',
                key: 'recording-list-type',
                sortable: true,
                renderCell: (row: RecordingListRow): string => getCallRecTypeValue(language, row)
            },
            {
                headerName: language.get(RecordingListLanguage.RecordingList_Column_TelSys),
                field: 'TelSysID',
                key: 'recording-list-telsys',
                sortable: true,
                renderCell: (row: RecordingListRow): string =>
                    getDescriptionOrIdValue(row.TelSysDescription, row.TelSysID)
            },
            {
                headerName: language.get(RecordingListLanguage.RecordingList_Column_Partition),
                field: 'PartIDInTelSys',
                key: 'recording-list-partition',
                sortable: true,
                renderCell: (row: RecordingListRow): string =>
                    getDescriptionOrIdValue(row.PartitionDescription, row.PartIDInTelSys)
            }
        ],
        [language]
    )

    const filterCMEIDForModal: string = listCmeId ?? userInfo?.Scope?.CMEnvironmentID ?? DEFAULT_CME_ID
    const filterTelSysIDForModal: Definable<number> =
        selectedRow?.TelSysID ??
        (isConcreteTelSysOrPartitionFilterValue(filters?.telephonyServer?.value)
            ? Number(filters?.telephonyServer?.value)
            : undefined)
    const filterPartitionIDForModal: Definable<string> = isVarNotEmpty(selectedRow)
        ? String(selectedRow.PartIDInTelSys)
        : undefined

    return (
        <div className='recording-list admin-list-container'>
            <PageTitle title={RouterLang.PageTitle_recording} />
            <div className='recording-list-table-container admin-table-list-container'>
                <AdminFilters
                    localeText={{
                        cmeLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_CME_LABEL),
                        telephonyServerLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_TELSYS_LABEL
                        ),
                        partitionLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_PARTITION_LABEL
                        ),
                        scopeLabel: language.get(RecordingListLanguage.RecordingList_Scope_Label),
                        codeTypeLabel: language.get(RecordingListLanguage.RecordingList_Type_Label),
                        includeChildCmeLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_INCLUDE_CHILD_CMES_LABEL
                        ),
                        searchLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_SEARCH_LABEL)
                    }}
                    urlBootstrap={urlBootstrap}
                    onChange={(values: AdminFilterValues): void => {
                        setFilters(values)

                        if (hasResolvedInitialTelSysPartitionListFilters(values)) {
                            setHasReceivedInitialFilters(true)
                        }
                    }}
                    optionsToDisplay={{
                        cme: true,
                        telephonyServer: true,
                        partition: true,
                        crmType: false,
                        scope: true,
                        codeType: true,
                        userType: false,
                        includeCmeChildren: true,
                        search: true,
                        includeRefresh: true
                    }}
                    onRefresh={getRecordingIntegrations}
                    scopeOptions={scopeOptions}
                    codeTypeOptions={typeOptions}
                />
                <DataTable<RecordingListRow, keyof RecordingListRow>
                    permissionName={AdminBackendPermissionNames.RECORDING}
                    localeText={{
                        addButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Add),
                        viewButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_View),
                        changeButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Change),
                        deleteButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Delete),
                        selectionActive: (count: number): string =>
                            language.getInterpolated(GenericAdminListLanguage.GenericAdminList_Footer_Selected, count),
                        noRows: language.get(GenericAdminListLanguage.GenericAdminList_Footer_NoRows)
                    }}
                    columns={columns}
                    data={recordingList ?? []}
                    defaultRowsPerPageOptions={[10, 25, 50, 100]}
                    defaultRowsPerPage={DEFAULT_ROWS_PER_PAGE}
                    totalCount={totalCount}
                    isLoading={loading > 0 || isWaitingForInitialUrlBootstrapLoad || isWaitingForInitialFiltersLoad}
                    paginationServer
                    sortServer
                    isSelectable
                    colIndexToTriggerClick={0}
                    onToolbarAddClick={(): void => {
                        setSelectedRow(null)
                        setViewOnly(false)
                        setModalOpen(true)
                    }}
                    onToolbarChangeClick={([selectedItem]: RecordingListRow[]): void => {
                        setSelectedRow(selectedItem ?? null)
                        setViewOnly(false)
                        setModalOpen(true)
                    }}
                    onToolbarViewClick={([selectedItem]: RecordingListRow[]): void => {
                        setSelectedRow(selectedItem ?? null)
                        setViewOnly(true)
                        setModalOpen(true)
                    }}
                    onToolbarDeleteClick={(selectedItems: RecordingListRow[]): void => {
                        setLoading((currentLoading: number): number => currentLoading + 1)
                        deleteCallRecordingIntegrationsMany(
                            selectedItems
                                .map((row: RecordingListRow): string => row.CallRecIntGUID)
                                .filter((guid: string): boolean => isStringNotEmpty(guid))
                        )
                            .then((): void => {
                                getRecordingIntegrations()
                            })
                            .catch((error: Error): void => {
                                displayError(error)
                            })
                            .finally((): void => {
                                if (isMounted.current) setLoading((currentLoading: number): number => currentLoading - 1)
                            })
                    }}
                    onSortChange={setSort}
                    onRowsPerPageChange={(currentPage: number, newRowsPerPage: number): void =>
                        setPagination([currentPage, newRowsPerPage])
                    }
                    onPageChange={(newPage: number, rowsPerPage: number): void => setPagination([newPage, rowsPerPage])}
                />
                {isModalOpen ? (
                    <ModifyCallRecIntegration
                        key={`${selectedRow?.CallRecIntGUID ?? 'new'}-${viewOnly ? 'v' : 'e'}`}
                        refreshAction={getRecordingIntegrations}
                        filteredCME={filterCMEIDForModal}
                        filterTelSysID={filterTelSysIDForModal}
                        filterPartitionID={filterPartitionIDForModal}
                        filteredIntegrationGUID={selectedRow?.CallRecIntGUID}
                        viewOnly={viewOnly}
                        onModalExited={(): void => {
                            setModalOpen(false)
                            setSelectedRow(null)
                        }}
                    />
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}
